/**
 * Looking action - Provides description of current location and visible items
 *
 * Uses four-phase pattern:
 * 1. validate: Always valid (basic sensory action)
 * 2. execute: Mark room as visited (only mutation)
 * 3. report: Generate success events with complete state snapshots
 * 4. blocked: Generate error events (never called since looking always valid)
 */

import { Action, ActionContext, ValidationResult } from '../../enhanced-types';
import { ISemanticEvent } from '@sharpee/core';
import { TraitType, RoomTrait } from '@sharpee/world-model';
import { IFActions } from '../../constants';
import { ActionMetadata } from '../../../validation';
import { captureRoomSnapshot } from '../../base/snapshot-utils';
import { emitIllustrations } from '../../helpers/emit-illustrations';
import { buildEventData } from '../../data-builder-types';
import { entityInfoFrom } from '../../../utils';
import {
  lookingEventDataConfig,
  roomDescriptionDataConfig,
  listContentsDataConfig,
  determineLookingMessage,
  ContainerContentsInfo
} from './looking-data';
import { LookingMessages } from './looking-messages';

export const lookingAction: Action & { metadata: ActionMetadata } = {
  id: IFActions.LOOKING,
  requiredMessages: [
    'room_description',
    'room_description_brief',
    'room_dark',
    'contents_list',
    'nothing_special',
    'in_container',
    'on_supporter',
    'cant_see_in_dark',
    'look_around',
    'examine_surroundings'
  ],
  
  validate(context: ActionContext): ValidationResult {
    // Looking is always valid - it's a basic sensory action
    return { valid: true };
  },
  
  execute(context: ActionContext): void {
    // Only mutation: mark room as visited
    const location = context.currentLocation;
    const room = context.world.getContainingRoom(context.player.id);
    
    if (room && room.hasTrait(TraitType.ROOM)) {
      const roomTrait = room.getTrait(RoomTrait);
      if (roomTrait && !roomTrait.visited) {
        roomTrait.visited = true;
      }
    }
    
    // No events returned - they're generated in report()
  },
  
  report(context: ActionContext): ISemanticEvent[] {
    // report() is only called on success - looking always succeeds
    const events: ISemanticEvent[] = [];

    // Build looked event data
    const lookedEventData = buildEventData(lookingEventDataConfig, context);

    // Determine message and params
    const isDark = lookedEventData.isDark as boolean;
    const { messageId, params } = determineLookingMessage(context, isDark);

    // If dark, emit looked event with dark messageId and return early
    if (isDark) {
      events.push(context.event('if.event.looked', {
        messageId: `${context.action.id}.${messageId}`,
        params,
        ...lookedEventData
      }));
      return events;
    }

    // Emit looked event as domain event (no messageId - specialized handler handles room description)
    const lookedEvent = context.event('if.event.looked', lookedEventData);
    events.push(lookedEvent);

    // Build and emit room description event (specialized handler renders this)
    const roomDescData = buildEventData(roomDescriptionDataConfig, context);
    events.push(context.event('if.event.room.description', roomDescData));

    // Emit illustration events for the room (ADR-124)
    const room = context.world.getContainingRoom(context.player.id);
    if (room) {
      events.push(...emitIllustrations(room, 'on-enter', lookedEvent.id, context));
    }

    // Build list contents data
    const listData = buildEventData(listContentsDataConfig, context);

    // Emit contents_list if there are direct items in the room
    if (params.hasItems) {
      events.push(context.event('if.event.list.contents', {
        messageId: `${context.action.id}.contents_list`,
        params,
        ...listData
      }));
    }

    // Emit messages for container/supporter contents
    const openContainerContents = listData.openContainerContents as ContainerContentsInfo[] | undefined;
    if (openContainerContents && openContainerContents.length > 0) {
      for (const containerInfo of openContainerContents) {
        const contentsMessageId = containerInfo.preposition === 'in'
          ? 'container_contents'
          : 'surface_contents';
        const containerKey = containerInfo.preposition === 'in' ? 'container' : 'surface';

        // params carry EntityInfo for the formatter chain (ADR-158);
        // top-level event fields stay strings for handler consumption.
        const containerEntity = context.world.getEntity(containerInfo.containerId);
        events.push(context.event('if.event.list.contents', {
          messageId: `${context.action.id}.${contentsMessageId}`,
          params: {
            [containerKey]: containerEntity
              ? entityInfoFrom(containerEntity)
              : { name: containerInfo.containerName },
            items: containerInfo.itemNames.join(', ')
          },
          containerId: containerInfo.containerId,
          containerName: containerInfo.containerName,
          preposition: containerInfo.preposition,
          itemIds: containerInfo.itemIds,
          itemNames: containerInfo.itemNames
        }));
      }
    }

    return events;
  },

  blocked(context: ActionContext, result: ValidationResult): ISemanticEvent[] {
    // blocked() is called when validation fails
    // Looking always succeeds, so this should never be called
    return [context.event('if.event.looked', {
      blocked: true,
      reason: result.error,
      messageId: `${context.action.id}.${result.error}`,
      params: result.params,
      actorId: context.player.id
    })];
  },

  group: "observation",
  
  metadata: {
    requiresDirectObject: false,
    requiresIndirectObject: false
  }
};
